import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimationPopup from "@/components/common/Popups/AnimationPopup";
import LoadingDots from "@/components/LoadingDots";

type Props = {
	showModal: boolean;
	loader: boolean;
	length?: number;
	invalid?: boolean;
	setShowModal: (showModal: boolean) => void;
	setInvalid?: (val: boolean) => void;
	onComplete: (pin: string) => void;
};

export default function SecurePinPopup({
	showModal,
	loader,
	length = 4,
	invalid = false,
	setShowModal,
	setInvalid,
	onComplete,
}: Props) {
	const { t } = useTranslation();

	const [values, setValues] = useState<Array<string>>(Array(length).fill(""));
	const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

	useEffect(() => {
		if (!showModal) {
			setValues(Array(length).fill(""));
			if (setInvalid) setInvalid(false);
		}
	}, [showModal, length, setInvalid]);

	const handleChange = (value: string) => {
		if (!/^\d$/.test(value)) return;

		if (invalid && setInvalid) setInvalid(false);

		const firstEmptyIndex = values.findIndex((v) => v === "");
		if (firstEmptyIndex === -1) return;

		const newValues = [...values];
		newValues[firstEmptyIndex] = value;
		setValues(newValues);

		if (firstEmptyIndex < length - 1) inputsRef.current[firstEmptyIndex + 1]?.focus();

		const fullPin = newValues.join("");
		if (!newValues.includes("") && fullPin.length === length) {
			onComplete(fullPin);
			setValues(Array(length).fill(""));
			inputsRef.current[0]?.focus();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace") {
			e.preventDefault();
			const lastFilledIndex =
				[...values]
					.map((v, i) => ({ v, i }))
					.reverse()
					.find((item) => item.v !== "")?.i ?? -1;
			if (lastFilledIndex !== -1) {
				const newValues = [...values];
				newValues[lastFilledIndex] = "";
				setValues(newValues);
				inputsRef.current[lastFilledIndex]?.focus();
			}
		}
	};
	return (
		<AnimationPopup
			showModal={showModal}
			setShowModal={() => setShowModal(false)}
			content={
				<div className="flex flex-col items-center text-center">
					<h4 className="font-semibold mt-2.5">{t("popup.Enter Store Access Code")}</h4>
					<p className="mb-8 sm:mb-16">{t("popup.This code is provided by store staff.")}</p>
					{loader ? (
						<div className="flex items-center h-[88px]">
							<LoadingDots />
						</div>
					) : (
						<div className="flex gap-6">
							{values.map((digit, idx) => (
								<input
									key={idx}
									ref={(el) => {
										inputsRef.current[idx] = el;
									}}
									type="text"
									inputMode="numeric"
									maxLength={1}
									value={digit ? "•" : ""}
									onChange={(e) => handleChange(e.target.value)}
									onKeyDown={handleKeyDown}
									className={`w-12 h-16 sm:w-[60px] sm:h-[88px] bg-white text-center pb-1 text-5xl text-darkgrey border-2 ${
										invalid ? "border-faulty" : "border-darkgrey"
									} rounded-lg focus:outline-none caret-transparent`}
								/>
							))}
						</div>
					)}
					<p className={`text-center text-faulty mt-4 ${invalid ? "visible" : "invisible"}`}>
						{t("popup.Incorrect code. Please try again.")}
					</p>
				</div>
			}
		/>
	);
}
